import React, { useMemo } from "react";
import { TagSelectV2, SkeletonLoader, UI_CAPTION, UI_BODY_SM, colors2 } from "oolib";

import { buildSignalCategoryTheme } from "./signalCategories";

/**
 * The two signal-slicing chip rows — "Signal categories" (over the tenant's
 * categories, PM four by default) and "Signals" (scoped to the selected
 * categories). Both multi-select, and within a row the picks are OR — any of
 * these categories, any of these signals — the filter module's rule.
 *
 * Purely presentational: it owns no state and does no data fetching. The
 * consumers keep their own selection state because they act on it very
 * differently — the Signals tab turns it into ES filters for a listing, the
 * Topics tab filters an already-clustered tree client-side.
 *
 * The two rows cannot contradict each other: the Signals row only lists
 * signals under the selected categories, and changing the categories drops
 * any selected signal that is no longer listed. So "signals selected" always
 * means a narrowing within the categories, and the consumers read the scope
 * as: the selected signals, else every signal of the selected categories,
 * else everything. That rule lives here so the consumers can't drift on it.
 *
 * Consumers that need a Dataset row render it themselves — deliberately not
 * in here: the two tabs count datasets differently (ES facets vs
 * observation-level tree counts), so only the signal rows are truly shared.
 */

const Row = ({ label, children }) => (
  <div style={{ marginBottom: "1rem" }}>
    <UI_CAPTION style={{ color: colors2.grey60, display: "block", marginBottom: "0.4rem" }}>
      {label}
    </UI_CAPTION>
    {children}
  </div>
);

const Chips = ({ children }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem" }}>{children}</div>
);

const toggled = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

export const SignalFilters = ({
  /** [{ _id, display, category, count? }] — every selectable signal. */
  signals = [],
  /** Category values picked, any of them. */
  selectedCategories = [],
  onChangeCategories,
  /** Signal _ids picked, any of them — always within the selected categories. */
  selectedSignals = [],
  onChangeSignals,
  /** Append "(n)" to each signal chip — the consumer supplies `count`. */
  showCounts = false,
  loading = false,
  emptyMessage = "No signals found.",
  /** Hide categories that no available signal belongs to. The Signals tab
   *  shows all four regardless (its chips scope an ES query); the Topics tab
   *  passes true so you can't filter a tree down to a guaranteed blank. */
  hideEmptyCategories = false,
  /** [{ value, display }] — the tenant's signal categories in display order
   *  (platformConfigs.SIGNAL_CATEGORIES / the tree's signalCategories).
   *  Omit for the PM defaults. */
  categories = null,
}) => {
  const theme = useMemo(() => buildSignalCategoryTheme(categories), [categories]);
  const categoriesPresent = new Set(signals.map((s) => s.category).filter(Boolean));
  const categoryOptions = hideEmptyCategories
    ? theme.options.filter((o) => categoriesPresent.has(o.value))
    : theme.options;

  // Signals row is scoped to the selected categories (all of them when none).
  const categorySet = new Set(selectedCategories);
  const visibleSignals = categorySet.size ? signals.filter((s) => categorySet.has(s.category)) : signals;
  const signalCategoryOf = Object.fromEntries(signals.map((s) => [s._id, s.category]));
  const toggleCategory = (value) => {
    const next = toggled(selectedCategories, value);
    onChangeCategories?.(next);
    // A signal outside the categories now picked is no longer listed, so it
    // cannot stay picked.
    const kept = next.length ? selectedSignals.filter((id) => next.includes(signalCategoryOf[id])) : selectedSignals;
    if (kept.length !== selectedSignals.length) onChangeSignals?.(kept);
  };

  return (
    <>
      <Row label="Signal categories">
        <Chips>
          {categoryOptions.map((opt) => (
            <TagSelectV2
              key={opt.value}
              display={opt.display}
              value={opt.value}
              color={opt.color}
              isSelected={selectedCategories.includes(opt.value)}
              onClick={() => toggleCategory(opt.value)}
            />
          ))}
        </Chips>
      </Row>

      <Row label="Signals">
        {loading ? (
          <SkeletonLoader style={{ width: "100%", height: "3.2rem" }} />
        ) : visibleSignals.length === 0 ? (
          <UI_BODY_SM style={{ color: colors2.grey60 }}>{emptyMessage}</UI_BODY_SM>
        ) : (
          <Chips>
            {visibleSignals.map((tag) => (
              <TagSelectV2
                key={tag._id}
                display={
                  showCounts && tag.count != null
                    ? `${tag.display} (${tag.count})`
                    : tag.display
                }
                value={tag._id}
                badgeColor={tag.category ? theme.fillOf(tag.category) : undefined}
                isSelected={selectedSignals.includes(tag._id)}
                onClick={() => onChangeSignals?.(toggled(selectedSignals, tag._id))}
              />
            ))}
          </Chips>
        )}
      </Row>
    </>
  );
};
